import { inject, injectable } from 'inversify';
import { IMapper } from '@Mapper/IMapper';
import { ArsenalArmyMapper } from '@Mapper/arsenal/arsenal-army.mapper';
import { TYPES } from '@Types';
import {
  RequestAction,
  RequestFailure,
  RequestSuccess,
} from '../RequestAction';
import {
  RequestArsenalArmiesSuccess,
  RequestArsenalArmySuccess,
  RequestArsenalArmyVersioningSuccess,
} from './response';
import CacheRepository from '../CacheRepository';
import { ArsenalArmy, Nullable } from '@Models';
import { ApiClient } from '@Api';
import { DeleteRepository } from '../DeleteRepository';
import { arsenal_army_full } from '@Api';
import { arsenal_army_all } from '@Api';
import { arsenal_army_last_update } from '@Api';

@injectable()
export class ArsenalArmyRepository
  extends CacheRepository<
    string,
    arsenal_army_last_update,
    arsenal_army_full,
    ArsenalArmy
  >
  implements DeleteRepository<string>
{
  @inject(TYPES.Mapper.ArsenalArmyMapper)
  private _modelArmyMapper!: ArsenalArmyMapper;
  @inject(TYPES.Api.ApiClient) private _apiClient!: ApiClient;

  constructor() {
    super({ key: `/arsenal/army` });
  }

  public async create(
    army: ArsenalArmy,
    { locale }: { locale: Nullable<string> },
  ): Promise<RequestAction> {
    try {
      const modelArmyRequestBody = this._modelArmyMapper.mapDto(army);

      const modelArmyDto = await this._apiClient.arsenal.createArmy({
        locale: this._locale.getlocale(),
        requestBody: modelArmyRequestBody,
      });
      await this.setCachedEntity({ data: modelArmyDto, locale: locale });
      return new RequestArsenalArmySuccess({
        payload: this._modelArmyMapper.mapFull(modelArmyDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async update({
    id,
    modelArmy,
    locale,
  }: {
    id: string;
    modelArmy: ArsenalArmy;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const modelArmyRequestBody = this._modelArmyMapper.mapDto(modelArmy);

      const modelArmyDto = await this._apiClient.arsenal.updateArmy({
        locale: this._locale.getlocale(),
        id: id,
        requestBody: modelArmyRequestBody,
      });
      await this.setCachedEntity({ data: modelArmyDto, locale: locale });
      return new RequestArsenalArmySuccess({
        payload: this._modelArmyMapper.mapFull(modelArmyDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async delete({
    id,
    locale,
  }: {
    id: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      await this._apiClient.arsenal.deleteArmy({
        locale: this._locale.getlocale(),
        id: id,
      });
      await this.removeCachedEntity({ entityId: id, locale: locale });
      return new RequestSuccess();
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async getAll({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const modelArmysDto = await this._apiClient.arsenal.getMyArmies({
        locale: this._locale.getlocale(),
      });

      await this.setCachedEntities({ data: modelArmysDto, locale: locale });
      return new RequestArsenalArmiesSuccess({
        payload: modelArmysDto.map((item) =>
          this._modelArmyMapper.mapAll(item),
        ),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async get(
    id: string,
    { locale }: { locale: Nullable<string> },
  ): Promise<RequestAction> {
    try {
      const modelArmyDto = await this._apiClient.arsenal.getArsenalArmy({
        locale: locale ?? this._locale.getlocale(),
        id: id,
      });

      await this.setCachedEntity({ data: modelArmyDto, locale: locale });
      return new RequestArsenalArmySuccess({
        payload: this._modelArmyMapper.map(modelArmyDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async getVersioning({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const itemsDto = await this._apiClient.arsenal.getUpdatedAtArsenalArmies({
        locale: locale ?? this._locale.getlocale(),
      });
      return new RequestArsenalArmyVersioningSuccess({
        payload: itemsDto.map((item) =>
          this._modelArmyMapper.mapVersioning(item),
        ),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  protected getMapper(): IMapper<
    string,
    arsenal_army_all,
    arsenal_army_full,
    ArsenalArmy
  > {
    return this._modelArmyMapper;
  }

  async getByArmyCategoryId(
    armyCategoryId: number,
    { locale }: { locale: null },
  ) {
    try {
      const modelArmyDto = await this._apiClient.arsenal.getByArmyCategoryId({
        armyCategoryId: armyCategoryId,
        locale: locale ?? this._locale.getlocale(),
      });

      return new RequestArsenalArmySuccess({
        payload: this._modelArmyMapper.map(modelArmyDto),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }
}
